import React from 'react'

import { Card as ShadcnCard } from 'src/components/ui/card'
import { cn } from 'src/lib/utils'

import type { CardProps } from './antd-props'
import { Skeleton } from './Skeleton.shadcn'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Card` seam. `./Card.tsx` is the dispatcher
 * that chooses between this and antd's own Card; nothing else imports this
 * file, and nothing reaches it until someone flips the design toggle
 * (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Card.tsx` (its
 * Phase 2 slice 11). Divergences from the reference are marked `[development]`
 * below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is antd's own `CardProps`, so the 167 files that already
 * import `Card` from `src/antd` switch with no edit at all.
 *
 * The shadcn Card is a COMPOSITION (Card / CardHeader / CardContent), while
 * antd's is a single component with `title` / `extra` / `cover` / `actions`
 * slots. This seam composes the shadcn root with antd's slot structure.
 *
 * antd prop mapping:
 *   size:       'small' → tighter head/body padding; 'default' → antd's 24px
 *   bordered:   false → no border (antd v5 keeps the shadow)
 *   type:       'inner' → the muted, nested-card head antd draws
 *   hoverable:  raises the shadow on hover
 *   loading:    replaces the BODY with a paragraph skeleton, as antd does —
 *               `./Skeleton.shadcn` DIRECTLY rather than the `./Skeleton`
 *               dispatcher, because this file only runs in shadcn mode: going
 *               through the seam would re-ask a question already answered and
 *               add a `useDesignMode` subscription per loading card
 *   bodyStyle / headStyle: antd's deprecated aliases for `styles.body` /
 *               `styles.header`, both still live here (8 and 1 call sites)
 *
 * [development] `.ant-card*` IS NOT RE-EMITTED, and this is the one place this
 * port diverges structurally from the reference. Over there antd is deleted,
 * so those names are ours and re-using them costs nothing. Here antd is
 * installed and the toggle is a RUNTIME flip: any page that rendered an antd
 * Card before the flip has antd's card stylesheet on it, UNLAYERED, and
 * `.ant-card` carries background, border, border-radius and font, while
 * `.ant-card-body` carries its own padding — i.e. exactly the properties this
 * seam replaces. That is the `.ant-btn` situation, not the `.ant-space` one.
 * So the parts are keyed by `data-slot` only, and the seven live stylesheets
 * that reached into antd's names each grew a `[data-slot='…']` selector beside
 * the `.ant-card-…` one (the same treatment `../Tag` gave its three).
 *
 * [development] `classNames` is honoured rather than stripped. The reference
 * drops it; antd's own Card forwards it per slot, and keeping it costs one
 * lookup per part — dropping a prop the API declares is the kind of thing that
 * only shows up as an unstyled corner months later.
 *
 * NOT PORTED, deliberately, and measured: `tabList` / `activeTabKey` /
 * `defaultActiveTabKey` / `tabBarExtraContent` / `onTabChange` / `tabProps` —
 * antd's tabbed-card variant. No call site in this repo passes any of them, and
 * implementing it would mean porting Tabs, which belongs to its own slice. They
 * are stripped here so they cannot reach the DOM as unknown attributes.
 * ------------------------------------------------------------------------- */

const CardBase = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      title,
      extra,
      cover,
      actions,
      children,
      className,
      style,
      size = 'default',
      bordered = true,
      hoverable,
      loading,
      type,
      bodyStyle,
      styles,
      headStyle,
      classNames: slotClassNames,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      tabList: _tabList,
      activeTabKey: _activeTabKey,
      defaultActiveTabKey: _defaultActiveTabKey,
      tabBarExtraContent: _tabBarExtraContent,
      onTabChange: _onTabChange,
      tabProps: _tabProps,
      ...domProps
    },
    ref
  ) => {
    const isSmall = size === 'small'
    const hasHead = title != null || extra != null

    return (
      <ShadcnCard
        ref={ref}
        className={cn(
          // No `overflow-hidden` on the root: antd's Card has none either, and
          // adding it would clip any non-portaled absolute content a card body
          // hosts. The cover clips itself instead.
          'gap-0 py-0',
          !bordered && 'border-0',
          hoverable && 'cursor-pointer transition-shadow hover:shadow-md',
          className
        )}
        data-size={isSmall ? 'small' : undefined}
        data-card-type={type}
        style={style}
        {...domProps}
      >
        {cover ? (
          <div
            data-slot="card-cover"
            className={cn(
              'overflow-hidden [&>img]:block [&>img]:w-full',
              slotClassNames?.cover
            )}
            style={styles?.cover}
          >
            {cover}
          </div>
        ) : null}

        {hasHead ? (
          <div
            data-slot="card-head"
            className={cn(
              'flex min-h-[48px] items-center justify-between gap-4 border-b font-semibold',
              isSmall ? 'px-3 py-2 text-sm' : 'px-6 py-4 text-base',
              type === 'inner' && 'bg-muted/50 font-normal',
              slotClassNames?.header
            )}
            style={{ ...headStyle, ...styles?.header }}
          >
            <div
              data-slot="card-head-title"
              className={cn('min-w-0 flex-1 truncate', slotClassNames?.title)}
              style={styles?.title}
            >
              {title}
            </div>
            {extra ? (
              <div
                data-slot="card-extra"
                className={cn('shrink-0 font-normal', slotClassNames?.extra)}
                style={styles?.extra}
              >
                {extra}
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          data-slot="card-body"
          className={cn(isSmall ? 'p-3' : 'p-6', slotClassNames?.body)}
          style={{ ...bodyStyle, ...styles?.body }}
        >
          {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : children}
        </div>

        {actions?.length ? (
          <ul
            data-slot="card-actions"
            className={cn(
              'flex list-none border-t p-0',
              slotClassNames?.actions
            )}
            style={styles?.actions}
          >
            {actions.map((action, i) => (
              <li
                // antd keys its actions by index too — they are a positional list.
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className="flex flex-1 items-center justify-center py-3 not-last:border-r"
              >
                {action}
              </li>
            ))}
          </ul>
        ) : null}
      </ShadcnCard>
    )
  }
)

CardBase.displayName = 'Card'

export const Card = CardBase
